import PropTypes from 'prop-types';
import { Button, Chip, Stack, Tooltip, Typography } from '@mui/material';

import Iconify from '../../../components/iconify';
import { DEFAULT_RECORDING_COLOR } from './recordingColors';

const SAVE_STATUS_ICON = {
  saving: { icon: 'eva:sync-outline', color: 'text.disabled' },
  saved: { icon: 'eva:checkmark-fill', color: 'success.main' },
  error: { icon: 'eva:alert-circle-outline', color: 'error.main' },
};

const SAVE_STATUS_TEXT = {
  saving: 'Saving…',
  saved: 'Saved',
  error: 'Save failed',
};

SaveStatus.propTypes = {
  status: PropTypes.oneOf(['idle', 'saving', 'saved', 'error']),
};

function SaveStatus({ status }) {
  if (status === 'idle') return null;
  const badge = SAVE_STATUS_ICON[status];
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mr: 0.5 }}>
      {badge && <Iconify icon={badge.icon} width={16} sx={{ color: badge.color }} />}
      <Typography
        variant="caption"
        sx={{ color: status === 'error' ? 'error.main' : 'text.secondary' }}
        aria-live="polite"
      >
        {SAVE_STATUS_TEXT[status]}
      </Typography>
    </Stack>
  );
}

WorkspaceToolbar.propTypes = {
  recordings: PropTypes.array,
  recordingMetadata: PropTypes.object,
  saveStatus: PropTypes.string,
  onOpenChip: PropTypes.func.isRequired,
  onAddRecording: PropTypes.func.isRequired,
  onAddPanel: PropTypes.func.isRequired,
};

/** The strip above the tabs: one chip per recording, save state, and the two add buttons. */
export default function WorkspaceToolbar({
  recordings = [],
  recordingMetadata = {},
  saveStatus,
  onOpenChip,
  onAddRecording,
  onAddPanel,
}) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ px: 1, py: 0.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0, bgcolor: 'background.default' }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>Recordings:</Typography>
        {recordings.map((rec) => {
          const meta = recordingMetadata[rec.id];
          const displayLabel = meta?.label || rec.name;
          return (
            <Tooltip key={rec.id} title="Click to rename or recolor">
              <Chip
                label={displayLabel}
                size="small"
                aria-label={`Recording ${displayLabel} — click to rename or recolor`}
                sx={{
                  bgcolor: meta?.color || DEFAULT_RECORDING_COLOR,
                  color: '#fff',
                  fontWeight: 500,
                  cursor: 'pointer',
                  '&:hover': { opacity: 0.85 },
                }}
                onClick={(e) => onOpenChip(rec.id, e.currentTarget, meta?.label || '')}
              />
            </Tooltip>
          );
        })}
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center">
        <SaveStatus status={saveStatus} />
        <Button size="small" variant="outlined" startIcon={<Iconify icon="eva:film-fill" />} onClick={onAddRecording}>
          Add Recording
        </Button>
        <Tooltip title={recordings.length === 0 ? 'Add a recording first' : ''}>
          <span>
            <Button
              size="small"
              variant="contained"
              startIcon={<Iconify icon="eva:plus-fill" />}
              onClick={onAddPanel}
              disabled={recordings.length === 0}
            >
              Add Panel
            </Button>
          </span>
        </Tooltip>
      </Stack>
    </Stack>
  );
}
